// ✅ src/screens/BuyGiftsScreen.js (FULL)
// - Auto seeds gifts
// - Auto creates wallets/{uid} if missing
// - Shows real coin balance (no more 0)
// - Paystack inline purchase -> increments wallet.coins
//
// ✅ Requires public/index.html:
// <script src="https://js.paystack.co/v1/inline.js"></script>
//
// ✅ .env:
// REACT_APP_PAYSTACK_KEY=pk_test_xxx (or pk_live_xxx)
// restart npm start after edit

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  increment,
} from "firebase/firestore";
import { auth, db } from "../lib/firebase";

/* ---------------- helpers ---------------- */
function safeStr(v) {
  return String(v ?? "").trim();
}

/**
 * ✅ Your rule:
 * 10 coins = ₵1.00 => 1 coin = ₵0.10
 */
const COIN_RATE_GHS = 0.1;

function coinsToGhs(coins) {
  const n = Number(coins ?? 0);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return +(n * COIN_RATE_GHS).toFixed(2);
}

function ghsToPesewas(ghs) {
  const n = Number(ghs ?? 0);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.round(n * 100); // Paystack smallest unit for GHS (pesewas)
}

/* ---------------- seed gifts ---------------- */
const SEED_GIFTS = [
  { id: "rose", name: "Rose", emoji: "🌹", coins: 10, sort: 1, isActive: true },
  { id: "tulip", name: "Tulip", emoji: "🌷", coins: 20, sort: 2, isActive: true },
  { id: "chocolate", name: "Chocolate", emoji: "🍫", coins: 25, sort: 3, isActive: true },
  { id: "icecream", name: "Ice Cream", emoji: "🍦", coins: 40, sort: 4, isActive: true },
  { id: "teddy", name: "Teddy Bear", emoji: "🧸", coins: 50, sort: 5, isActive: true },
  { id: "perfume", name: "Perfume", emoji: "🧴", coins: 75, sort: 6, isActive: true },
  { id: "ring", name: "Diamond Ring", emoji: "💍", coins: 100, sort: 7, isActive: true },
  { id: "bouquet", name: "Bouquet", emoji: "💐", coins: 150, sort: 8, isActive: true },
  { id: "heels", name: "Heels", emoji: "👠", coins: 200, sort: 9, isActive: true },
  { id: "crown", name: "Crown", emoji: "👑", coins: 250, sort: 10, isActive: true },
  { id: "giftbox", name: "Gift Box", emoji: "🎁", coins: 300, sort: 11, isActive: true },
  { id: "diamond", name: "Diamond", emoji: "💎", coins: 500, sort: 12, isActive: true },
  { id: "car", name: "Car", emoji: "🚗", coins: 750, sort: 13, isActive: true },
  { id: "rocket", name: "Rocket", emoji: "🚀", coins: 1000, sort: 14, isActive: true },
  { id: "yacht", name: "Yacht", emoji: "🛥️", coins: 1500, sort: 15, isActive: true },
  { id: "mansion", name: "Mansion", emoji: "🏠", coins: 2000, sort: 16, isActive: true },
  { id: "castle", name: "Castle", emoji: "🏰", coins: 2500, sort: 17, isActive: true },
  { id: "privatejet", name: "Private Jet", emoji: "🛩️", coins: 3500, sort: 18, isActive: true },
  { id: "galaxy", name: "Galaxy", emoji: "🌌", coins: 5000, sort: 19, isActive: true },
];

async function ensureGiftsSeed() {
  const giftsCol = collection(db, "gifts");
  const s = await getDocs(query(giftsCol, limit(1)));
  if (!s.empty) return;

  await Promise.all(
    SEED_GIFTS.map((g) =>
      setDoc(
        doc(db, "gifts", g.id),
        { ...g, createdAt: serverTimestamp(), updatedAt: serverTimestamp() },
        { merge: true }
      )
    )
  );
}

/* ---------------- wallet: auto create ---------------- */
async function ensureWalletDoc(uid, email) {
  const u = safeStr(uid);
  if (!u) return;

  const wRef = doc(db, "wallets", u);
  const snap = await getDoc(wRef);
  if (snap.exists()) return;

  await setDoc(
    wRef,
    {
      uid: u,
      email: safeStr(email),
      // ✅ coins user can spend to send gifts
      coins: 0,

      // ✅ earnings in GHS (from received gifts, referrals, etc.)
      balance: 0,
      pending: 0,
      totalEarned: 0,
      totalWithdrawn: 0,

      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

/* ---------------- screen ---------------- */
export default function BuyGiftsScreen() {
  const nav = useNavigate();

  const [me, setMe] = useState(null);
  const [coins, setCoins] = useState(0);

  const [gifts, setGifts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [payingId, setPayingId] = useState("");
  const [error, setError] = useState("");

  const giftsQ = useMemo(
    () => query(collection(db, "gifts"), orderBy("sort", "asc")),
    []
  );

  // auth
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        nav("/login", { replace: true });
        return;
      }
      setMe({ uid: u.uid, email: u.email || "" });
      setError("");

      // ✅ ensure wallet exists so UI never shows 0 due to missing doc
      try {
        await ensureWalletDoc(u.uid, u.email);
      } catch (e) {
        console.error("ensureWalletDoc error:", e);
      }
    });
    return () => unsub();
  }, [nav]);

  // wallet listener -> coins badge
  useEffect(() => {
    if (!me?.uid) return;

    const wRef = doc(db, "wallets", me.uid);
    const unsub = onSnapshot(
      wRef,
      (snap) => {
        if (!snap.exists()) {
          setCoins(0);
          return;
        }
        const w = snap.data() || {};
        setCoins(Number(w.coins ?? 0) || 0);
      },
      (e) => console.error("wallet listen error:", e)
    );

    return () => unsub();
  }, [me?.uid]);

  // gifts listener (auto-seed first)
  useEffect(() => {
    let unsub = null;

    (async () => {
      setLoading(true);
      try {
        await ensureGiftsSeed();
        unsub = onSnapshot(
          giftsQ,
          (snap) => {
            const arr = [];
            snap.forEach((d) => {
              const g = d.data() || {};
              if (g.isActive === false) return;
              arr.push({
                id: d.id,
                name: safeStr(g.name),
                emoji: safeStr(g.emoji) || "🎁",
                coins: Number(g.coins || 0),
                sort: Number(g.sort || 999),
              });
            });
            setGifts(arr);
            setLoading(false);
          },
          (e) => {
            console.error("gifts listen error:", e);
            setLoading(false);
          }
        );
      } catch (e) {
        console.error("ensureGiftsSeed error:", e);
        setLoading(false);
      }
    })();

    return () => {
      if (typeof unsub === "function") unsub();
    };
  }, [giftsQ]);

  async function addCoinsToWallet({ uid, addCoins, payRef, amountGhs }) {
    const walletRef = doc(db, "wallets", uid);

    await runTransaction(db, async (tx) => {
      const wSnap = await tx.get(walletRef);
      if (!wSnap.exists()) {
        tx.set(
          walletRef,
          {
            uid,
            coins: 0,
            balance: 0,
            pending: 0,
            totalEarned: 0,
            totalWithdrawn: 0,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      }

      tx.set(
        doc(db, "coinPayments", payRef),
        {
          uid,
          coins: addCoins,
          amountGhs: Number(amountGhs || 0),
          reference: payRef,
          status: "success_callback",
          createdAt: serverTimestamp(),
        },
        { merge: true }
      );

      tx.set(
        doc(db, "wallets", uid),
        {
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      tx.update(walletRef, {
        coins: increment(addCoins),
        updatedAt: serverTimestamp(),
      });
    });
  }

  async function startPaystackBuyCoins(g) {
    setError("");
    if (!me?.uid) return setError("Please sign in first.");

    const publicKey = safeStr(process.env.REACT_APP_PAYSTACK_KEY);
    if (!publicKey) return setError("Missing REACT_APP_PAYSTACK_KEY in .env (restart npm start).");

    if (!window?.PaystackPop) return setError("Paystack not loaded. Add script tag in public/index.html then restart.");

    const buyCoins = Number(g?.coins ?? 0);
    if (!Number.isFinite(buyCoins) || buyCoins <= 0) return setError("Invalid coin pack.");

    const amountGhs = coinsToGhs(buyCoins);
    const amount = ghsToPesewas(amountGhs);

    const email = safeStr(me?.email) || "customer@email.com";
    const reference = `coins_${me.uid}_${buyCoins}_${Date.now()}`;

    setPayingId(g.id);

    const handleSuccess = async (ref) => {
      try {
        await addCoinsToWallet({
          uid: me.uid,
          addCoins: buyCoins,
          payRef: safeStr(ref || reference),
          amountGhs,
        });
        alert(`✅ Coins added!\n+${buyCoins} coins`);
      } catch (e) {
        console.error(e);
        setError("Payment succeeded but saving coins failed. Check Firestore rules.");
      } finally {
        setPayingId("");
      }
    };

    try {
      const handler = window.PaystackPop.setup({
        key: publicKey,
        email,
        amount,
        currency: "GHS",
        ref: reference,
        callback: function (response) {
          const ref = safeStr(response?.reference) || reference;
          handleSuccess(ref);
        },
        onClose: function () {
          setPayingId("");
        },
        metadata: {
          uid: me.uid,
          type: "COINS",
          coins: buyCoins,
        },
      });

      handler.openIframe();
    } catch (e) {
      console.error(e);
      setPayingId("");
      setError("Failed to open Paystack. Check console for details.");
    }
  }

  return (
    <div style={S.page}>
      <div style={S.top}>
        <button type="button" style={S.backBtn} onClick={() => nav(-1)} title="Back">
          ←
        </button>

        <div style={{ minWidth: 0 }}>
          <div style={S.h1}>Buy Gifts</div>
          <div style={S.sub}>Buy coins, then send gifts in chat.</div>
        </div>

        <div style={{ marginLeft: "auto" }}>
          <div style={S.coinPill} title="Your coin balance">
            🪙 <b>{coins}</b> coins
          </div>
        </div>
      </div>

      {error ? (
        <div style={S.errBox}>
          <div style={S.errTitle}>Heads up</div>
          <div style={S.errText}>{error}</div>
        </div>
      ) : null}

      {loading ? (
        <div style={S.loading}>Loading…</div>
      ) : (
        <div style={S.grid}>
          {gifts.map((g) => {
            const priceGhs = coinsToGhs(g.coins);
            return (
              <div key={g.id} style={S.card}>
                <div style={S.emoji}>{g.emoji}</div>
                <div style={S.name}>{g.name}</div>

                <div style={S.price}>
                  <span style={{ fontWeight: 1000 }}>{g.coins} coins</span>
                  <span style={{ opacity: 0.55 }}> • </span>
                  <span style={{ fontWeight: 950 }}>₵{priceGhs.toFixed(2)}</span>
                </div>

                <button
                  type="button"
                  style={{ ...S.buyBtn, ...(payingId === g.id ? S.buyBtnDisabled : null) }}
                  disabled={payingId === g.id}
                  onClick={() => startPaystackBuyCoins(g)}
                >
                  {payingId === g.id ? "Opening Paystack…" : "Buy coins"}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ---------------- styles ---------------- */
const S = {
  page: {
    minHeight: "100vh",
    padding: 16,
    background:
      "radial-gradient(1200px 650px at 18% 0%, rgba(236,72,153,.14), transparent 60%), " +
      "radial-gradient(900px 520px at 90% 18%, rgba(99,102,241,.14), transparent 55%), " +
      "linear-gradient(180deg,#fbfaf8 0%, #f7f2ee 55%, #fbf7f3 100%)",
  },
  top: {
    maxWidth: 980,
    margin: "0 auto 14px",
    display: "flex",
    gap: 12,
    alignItems: "center",
  },
  backBtn: {
    width: 42,
    height: 42,
    borderRadius: 14,
    border: "1px solid rgba(2,6,23,0.10)",
    background: "rgba(255,255,255,0.85)",
    cursor: "pointer",
    fontWeight: 1000,
  },
  h1: { fontWeight: 1000, fontSize: 22, letterSpacing: -0.2 },
  sub: { fontWeight: 900, opacity: 0.65, fontSize: 13, marginTop: 2 },
  coinPill: {
    borderRadius: 999,
    border: "1px solid rgba(2,6,23,0.10)",
    background: "rgba(255,255,255,0.80)",
    padding: "10px 12px",
    fontWeight: 950,
    boxShadow: "0 14px 34px rgba(2,6,23,0.08)",
  },
  errBox: {
    maxWidth: 980,
    margin: "0 auto 14px",
    borderRadius: 18,
    border: "1px solid rgba(239,68,68,0.20)",
    background: "rgba(239,68,68,0.08)",
    padding: 12,
  },
  errTitle: { fontWeight: 1000, marginBottom: 4 },
  errText: { fontWeight: 900, opacity: 0.85, fontSize: 13 },
  loading: { maxWidth: 980, margin: "0 auto", fontWeight: 900, padding: 16 },

  grid: {
    maxWidth: 980,
    margin: "0 auto",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))",
    gap: 14,
  },
  card: {
    borderRadius: 20,
    border: "1px solid rgba(2,6,23,0.07)",
    background: "rgba(255,255,255,0.82)",
    padding: 14,
    display: "grid",
    gap: 8,
    boxShadow: "0 14px 34px rgba(2,6,23,0.06)",
    textAlign: "center",
  },
  emoji: { fontSize: 46, lineHeight: "46px" },
  name: { fontWeight: 1000 },
  price: { fontWeight: 900, opacity: 0.75, fontSize: 13 },

  buyBtn: {
    marginTop: 6,
    border: "none",
    borderRadius: 14,
    padding: "10px 12px",
    cursor: "pointer",
    fontWeight: 1000,
    background: "rgba(255,255,255,0.92)",
    borderTop: "1px solid rgba(2,6,23,0.06)",
    boxShadow: "0 12px 28px rgba(2,6,23,0.08)",
  },
  buyBtnDisabled: { opacity: 0.7, cursor: "not-allowed" },
};
